import { QUIZ_LIMITS_REACHED_ERROR } from '@showbie-socrative/socrative-utils';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useAtom, useSetAtom } from 'jotai';

import { CreateQuizEventAdapter } from '~analytics/adapters/CreateQuizEventAdapter';
import { PaywallScreens } from '~analytics/types';
import { translate } from '~l10n/client';
import {
  CurrentQuizEditorDialog,
  currentQuizEditorDialogAtom,
} from '~teacher/components/QuizEditor/atoms';
import { extractQuestionsPositionAtom } from '~teacher/components/QuizEditor/ExtractQuestionsDialog';
import { positionAtom as generateQuestionDialogPositionAtom } from '~teacher/components/QuizEditor/GenerateQuestionDialog/atom';
import { positionAtom as importFromTextPositionAtom } from '~teacher/components/QuizEditor/ImportFromTextDialog';
import { useSocrativeRouter } from '~teacher/context/RouterContext';

import { queryQuizLimit, useCreateQuizMutation } from '../../../hook';
import { PaywallDispatch } from '../../PaywallDialog';

interface PopupController {
  render: (param: { title: string; message: string }) => void;
}

interface CreateAddQuizParam {
  parentId: number;
  popupController: PopupController;
  quizLimitPaywallDispatch: PaywallDispatch;
}

export function useCreateQuiz({
  parentId,
  popupController,
  quizLimitPaywallDispatch,
}: CreateAddQuizParam) {
  const [, setGenerateQuestionPosition] = useAtom(
    generateQuestionDialogPositionAtom
  );

  const setImportFromTextPosition = useSetAtom(importFromTextPositionAtom);
  const setExtractQuestionsPosition = useSetAtom(extractQuestionsPositionAtom);

  const setCurrentQuizEditorDialog = useSetAtom(currentQuizEditorDialogAtom);

  const createQuizMutation = useCreateQuizMutation();
  const queryClient = useQueryClient();
  const router = useSocrativeRouter();

  return async (options?: {
    aiFeature?: 'generate' | 'import' | 'extract';
    onPaywallOpen?: () => void;
  }) => {
    const quizLimit = await queryQuizLimit(queryClient);

    if (quizLimit.currentQuizCount + 1 > quizLimit.limit) {
      quizLimitPaywallDispatch({
        type: 'OPEN',
        touch: [
          PaywallScreens.QuizListLimitReached,
          PaywallScreens.QuizListAddQuiz,
        ],
      });
    } else {
      createQuizMutation.mutate(
        { parentId: parentId },
        {
          onError: (error: any) => {
            if (error.isAxiosError) {
              const errorData = (error as AxiosError).response.data;
              if (errorData.code === QUIZ_LIMITS_REACHED_ERROR.code) {
                quizLimitPaywallDispatch({
                  type: 'OPEN',
                  touch: [
                    PaywallScreens.QuizListLimitReached,
                    PaywallScreens.QuizListAddQuiz,
                  ],
                });
              }
            } else {
              // This is just a placeholder until we have something common for these modals...
              popupController.render({
                title: translate('Unknown Error'),
                message: translate(
                  'An unknown error occurred. Please try again later.'
                ),
              });
            }
          },
          onSuccess: ({ socNumber }) => {
            let creationMethod = 'app' as any;

            if (options.aiFeature === 'generate') {
              setCurrentQuizEditorDialog(
                CurrentQuizEditorDialog.GenerateQuestions
              );
              setGenerateQuestionPosition(1);
              creationMethod = 'create_from_ai';
            } else if (options.aiFeature === 'import') {
              setCurrentQuizEditorDialog(
                CurrentQuizEditorDialog.ImportFromText
              );
              setImportFromTextPosition(1);
              creationMethod = 'create_from_ai_import';
            } else if (options.aiFeature === 'extract') {
              setCurrentQuizEditorDialog(
                CurrentQuizEditorDialog.ExtractQuestionsFromDoc
              );
              setExtractQuestionsPosition(1);
              creationMethod = 'create_from_ai_extract';
            } else {
              // Ensure all dialogs are closed!
              setCurrentQuizEditorDialog(CurrentQuizEditorDialog.None);
            }

            new CreateQuizEventAdapter({
              creation_method: creationMethod,
            }).sendAnalytics();
            router.routeToEditQuiz(socNumber, 2);
          },
        }
      );
    }
  };
}
